import { useEffect, useState } from "react";
import { Shield, Bell, Palette, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { ROLE_LABEL } from "@/lib/constants";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";
import { getTheme, setTheme } from "@/lib/theme";

export default function Profilo() {
  const { user } = useAuth();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [dark, setDark] = useState(getTheme() === "dark");

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  const togglePush = async (v) => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (v) { await enablePush(); setPushOn(true); toast.success("Notifiche attivate"); }
      else { await disablePush(); setPushOn(false); toast.success("Notifiche disattivate"); }
    } catch (e) {
      toast.error(e.message || "Errore notifiche");
    } finally {
      setPushBusy(false);
    }
  };

  const toggleTheme = (v) => {
    setDark(v);
    setTheme(v ? "dark" : "light");
    toast.success(v ? "Tema scuro attivato" : "Tema chiaro attivato");
  };

  const Row = ({ icon: Icon, title, desc, children }) => (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-4 last:border-0">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><Icon size={16} /></span>
        <div>
          <p className="font-semibold text-navy">{title}</p>
          <p className="text-sm text-muted-foreground">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="profilo-page">
      <PageHeader overline="Flux · Account" title="Profilo" subtitle="Le tue informazioni e preferenze personali" />

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="mb-2 font-bold text-navy">Account</h3>
        <Row icon={Shield} title={user?.name} desc={user?.email}>
          <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700">{ROLE_LABEL[user?.role]}</span>
        </Row>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="mb-2 font-bold text-navy">Preferenze</h3>
        <Row icon={Bell} title="Notifiche" desc={pushSupported() ? "Avvisi push su nuovi ticket e cambi di stato" : "Non supportate su questo browser/dispositivo"}>
          <div className="flex items-center gap-2">
            {pushBusy && <Loader2 size={15} className="animate-spin text-muted-foreground" />}
            <Switch checked={pushOn} onCheckedChange={togglePush} disabled={pushBusy || !pushSupported()} data-testid="pref-notifiche-switch" />
          </div>
        </Row>
        <Row icon={Palette} title="Tema" desc={dark ? "Aspetto dell'interfaccia (scuro)" : "Aspetto dell'interfaccia (chiaro)"}>
          <Switch checked={dark} onCheckedChange={toggleTheme} data-testid="pref-tema-switch" />
        </Row>
      </div>
    </div>
  );
}
